import { useEffect, useRef } from 'react'

import { F0Alert } from '@factorialco/f0-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import MoneyWithCurrency from 'modules/core/lib/money'
import i18n from 'modules/core/lib/i18n'
import { formatPercentage } from 'modules/project_management/lib/formatPercentage'

type AlertVariant = 'info' | 'warning' | 'critical'

interface EstimatedProfitMarginProps {
  estimatedCost?: number
  feeAmount?: number
  currency?: string
}

function getAlertVariant(marginRatio: number): AlertVariant {
  const rounded = Math.round(marginRatio * 100) / 100
  if (rounded < 0) return 'critical'
  if (rounded < 0.3) return 'warning'
  return 'info'
}

const EstimatedProfitMargin = ({
  estimatedCost,
  feeAmount,
  currency,
}: EstimatedProfitMarginProps) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const ref = useRef<HTMLDivElement>(null)
  const prevIsVisibleRef = useRef(false)

  const isVisible = estimatedCost != null && feeAmount != null && feeAmount !== 0

  useEffect(() => {
    if (isVisible && !prevIsVisibleRef.current) {
      const timer = setTimeout(() => {
        ref.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
      }, 0)

      prevIsVisibleRef.current = true
      return () => clearTimeout(timer)
    }
    prevIsVisibleRef.current = isVisible
  }, [isVisible])

  if (!isVisible) {
    return null
  }

  const estimatedProfit = feeAmount - estimatedCost
  const estimatedMarginRatio = estimatedProfit / feeAmount

  const formattedAmount = MoneyWithCurrency({
    amount: Math.round(estimatedProfit * 100),
    currency,
    locale,
  })
  const formattedPercentage = formatPercentage(estimatedMarginRatio)
  const description = `${formattedAmount} (${formattedPercentage})`

  return (
    <div ref={ref}>
      <F0Alert
        title={i18n.t('project_management.forms.financial_details.estimated_profit_margin')}
        description={description}
        variant={getAlertVariant(estimatedMarginRatio)}
      />
    </div>
  )
}

export default EstimatedProfitMargin
